import {
  CustomerApiForFiles,
  DedicatedApi,
  ICustomerListVariables,
} from "@/api-client";
import { reactProxyFactory } from "@/components/shared/ReactProxy.component";
import { SelectedDataExtractor } from "@/components/shared/table/selected-data-extractor";
import { IItem } from "@/components/shared/table/types";
import { AccessControlType, SortOrderType } from "@/generated/types";
import { ICopies } from "@/locales/copies";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import { IApiState } from "@/models/api";
import { ApiSchema } from "@/models/api-schema";
import { GraphqlApiSchemaHandler } from "@/models/api-schema/graphql-api-schema-handler";
import { getErrorMessage } from "@/utils";
import { ListDataManager } from "@/utils/list-data-manager";
import {
  FileExplorer as FileExplorerReact,
  IFileExplorerProps,
} from "@graphapi-io/customer-components";
import { IFileState } from "@graphapi-io/customer-components/dist/lib/files/types";
import { ApolloClient } from "apollo-boost";
import { FunctionComponent } from "react";
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";

const FileExplorer = reactProxyFactory<
  FunctionComponent<IFileExplorerProps>,
  IFileExplorerProps
>(FileExplorerReact as FunctionComponent<IFileExplorerProps>);

const getInitialVariables = (): ICustomerListVariables => ({
  limit: 100,
  nextToken: null,
  filter: undefined,
  sortOrder: SortOrderType.DESC,
  sortRange: undefined,
});

@Component
export default class ProjectAssetsExplorer extends UserPermissions {
  @Prop({ required: true }) value: IApiState;
  @Prop({ required: true }) apiClient: ApolloClient<unknown>;
  @Prop({ required: true }) apiSchemaHandler: GraphqlApiSchemaHandler | null;
  private inputFiles: File[] | null = null;
  private uploadProgress = 0;

  private get listDataManager() {
    return Vue.observable(
      new ListDataManager<
        { items: IItem[]; nextToken?: string | null },
        ICustomerListVariables
      >(
        this.customerApiForFiles.watchFileDocuments.bind(
          this.customerApiForFiles,
        ),
      ),
    );
  }

  private selectedDataExtractor = new SelectedDataExtractor(
    [],
    [],
    true,
    false,
  );

  private get schemaFieldTypes() {
    return this.apiSchemaHandler?.inProgress || !this.apiSchemaHandler
      ? {}
      : this.apiSchemaHandler?.schemaFieldTypes;
  }

  private get customerApi() {
    return new DedicatedApi(
      this.apiClient,
      this.schemaFieldTypes,
      new ApiSchema(this.value, this.schemaFieldTypes),
    );
  }

  private get customerApiForFiles() {
    return new CustomerApiForFiles(this.customerApi);
  }

  @Watch("apiSchemaHandler.inProgress", { immediate: true })
  protected onApiSchemaHandlerInitialized() {
    if (this.apiSchemaHandler && !this.apiSchemaHandler?.inProgress) {
      this.listDataManager.subscribeToList(getInitialVariables());
    }
  }

  private async handleFileDropped(file: File | File[] | null) {
    if (!file) return;

    this.inputFiles = Array.isArray(file) ? file : [file];

    if (this.inputFiles.length === 0) return;

    try {
      this.uploadProgress = 10;
      const intents = await Promise.all(
        this.inputFiles.map(async (inputFile) => {
          const intent = await this.customerApi.uploadFileIntent({
            input: {
              key: inputFile.name,
              mimeType: inputFile.type || undefined,
              accessControl: AccessControlType.PUBLIC,
            },
          });

          if (!intent) {
            throw new Error("No intent returned");
          }

          const response = await fetch(intent.uploadUrl, {
            method: "PUT",
            body: inputFile,
            headers: {
              "Content-Type": intent.mimeType,
            },
          });

          if (!response.ok) {
            throw new Error("Upload failed");
          }
          this.uploadProgress += (100 / (this.inputFiles?.length ?? 1)) * 0.8;
          return intent;
        }),
      );

      this.uploadProgress = 90;

      this.customerApiForFiles.batchCreateFileDocumentsFromIntents(
        intents,
        getInitialVariables(),
      );

      this.uploadProgress = 100;
    } catch (e) {
      this.listDataManager.errorMessage = getErrorMessage(e);
    } finally {
      this.$nextTick(() => {
        this.uploadProgress = 0;
      });
    }
  }

  private async handleLoadMore() {
    console.log("load more");
    if (!this.listDataManager) return;

    if (this.listDataManager.listVariables?.nextToken) {
      this.listDataManager.fetchMore?.({
        ...getInitialVariables(),
        nextToken: this.listDataManager.listVariables?.nextToken,
      });
    }
  }

  private async onDeleteEntries(fileIds: Record<string, unknown>[]) {
    try {
      if (this.hasAdminPermissions) {
        if (
          window.confirm(
            this.$t("api_details_data_delete_entries_confirm", {
              numOfEntries:
                fileIds.length > 1
                  ? this.$t("global_many_entries", {
                      numOfEntries: fileIds.length,
                    })
                  : this.$t("global_single_entry"),
              typeName: "Files",
            }).toString(),
          )
        ) {
          await this.customerApiForFiles.batchDeleteFileDocuments(
            fileIds,
            getInitialVariables(),
          );
        }
      } else {
        throw new Error(
          this.$t("error_api_management_requires_role").toString(),
        );
      }
    } catch (err) {
      this.listDataManager.errorMessage = getErrorMessage(err);
    }
  }

  private get fileExplorerProps(): IFileExplorerProps | undefined {
    const actionsCopies = this.$t(
      `fileExplorer.actions`,
    ) as unknown as ICopies["fileExplorer"]["actions"];

    const fileInputCopies = this.$t(
      `fileExplorer.fileInput`,
    ) as unknown as ICopies["fileExplorer"]["fileInput"];

    const emptyStateCopies = this.$t(
      `fileExplorer.emptyState`,
    ) as unknown as ICopies["fileExplorer"]["emptyState"];

    return {
      files: this.listDataManager.data as unknown as IFileState[],
      isLoading:
        this.listDataManager.listVariables === null ||
        this.listDataManager.isLoading,
      isUploading: this.uploadProgress > 0 && this.uploadProgress < 100,
      fileError: this.listDataManager.errorMessage,
      hasMoreItems: !!this.listDataManager.listVariables?.nextToken,
      onDeleteEntries: this.onDeleteEntries,
      onFilesDropped: this.handleFileDropped,
      onLoadMore: this.handleLoadMore,
      copies: {
        fileExplorer: {
          title: this.$t(`fileExplorer.title`).toString(),
          fileSectionTitle: this.$t(`fileExplorer.fileSectionTitle`).toString(),
          loadMore: this.$t(`fileExplorer.loadMore`).toString(),
          emptyState: {
            ...emptyStateCopies,
          },
          fileInput: {
            ...fileInputCopies,
          },
          actions: {
            ...actionsCopies,
          },
        },
      },
    };
  }

  render() {
    return this.fileExplorerProps ? (
      <FileExplorer componentProps={this.fileExplorerProps} />
    ) : null;
  }
}
